// effects/presets.ts

import type { World } from "../ecs/core";
import { pushEffect } from "../ecs/core";
import { getTheme } from "../visuals/theme";

export function triggerPlayerDeath(
  world: World,
  x: number,
  y: number,
  _vx: number = 0,
  _vy: number = 0,
  intensity: number = 1.0,
): void {
  const theme = getTheme();

  pushEffect(world, {
    kind: "webgl.gpuExplosion",
    x,
    y,
    intensity: intensity,
    preset: "large",
  });

  pushEffect(world, {
    kind: "screen.shake",
    intensity: 10 * intensity,
  });

  pushEffect(world, {
    kind: "screen.flash",
    intensity: 0.8 * intensity,
    r: 1.0,
    g: 0.2,
    b: 0.2,
  });

  pushEffect(world, {
    kind: "webgl.bloomSpike",
    intensity: theme.postprocess.bloom.intensity * 1.5,
    threshold: 0.0,
  });

  const normalizedX = x / world.deps.state.world.w;
  const normalizedY = y / world.deps.state.world.h;
  pushEffect(world, {
    kind: "webgl.radialBlur",
    intensity: 0.4 * intensity,
    x: normalizedX,
    y: normalizedY,
  });

  pushEffect(world, {
    kind: "sound",
    sound: "impact",
  });
}

export function triggerAsteroidDestruction(
  world: World,
  size: "small" | "medium" | "large",
  x: number,
  y: number,
  _vx: number = 0,
  _vy: number = 0,
): void {
  const theme = getTheme();

  pushEffect(world, {
    kind: "webgl.gpuExplosion",
    x,
    y,
    intensity: 1.0,
    preset: size,
    colorPalette: theme.particles.explosion.dustColors,
  });

  if (size === "large") {
    pushEffect(world, {
      kind: "screen.shake",
      intensity: 3.0,
    });
  }

  pushEffect(world, {
    kind: "webgl.bloomSpike",
    intensity: 2.2,
    threshold: 0.15,
  });

  pushEffect(world, {
    kind: "sound",
    sound: "impact",
  });
}

export function triggerMineExplosion(
  world: World,
  x: number,
  y: number,
  radius: number,
): void {
  const theme = getTheme();

  const radiusScale = radius / 80;
  pushEffect(world, {
    kind: "webgl.gpuExplosion",
    x,
    y,
    intensity: 1.0,
    radius: radius,
    preset: "large",
  });

  const shakeIntensity = Math.min(15 * radiusScale, 25);
  pushEffect(world, {
    kind: "screen.shake",
    intensity: shakeIntensity,
  });

  const flashIntensity = Math.min(0.3 * radiusScale, 0.5);
  pushEffect(world, {
    kind: "screen.flash",
    intensity: flashIntensity,
    r: 1.0,
    g: 0.6,
    b: 0.2,
  });

  const normalizedX = x / world.deps.state.world.w;
  const normalizedY = y / world.deps.state.world.h;
  pushEffect(world, {
    kind: "webgl.heatDistortion",
    x: normalizedX,
    y: normalizedY,
    intensity: 2.0,
    radius: radius / world.deps.state.world.w,
  });

  pushEffect(world, {
    kind: "webgl.bloomSpike",
    intensity: theme.postprocess.bloom.intensity * 2.0,
    threshold: 0.0,
  });

  pushEffect(world, {
    kind: "webgl.chromaticSpike",
    intensity: 8.0,
  });

  pushEffect(world, {
    kind: "sound",
    sound: "impact",
  });
}

export function triggerPelletCollection(
  world: World,
  value: number,
  x: number,
  y: number,
): void {
  pushEffect(world, {
    kind: "sound",
    sound: "pickup",
    amount: value,
  });

  if (value >= 10) {
    pushEffect(world, {
      kind: "webgl.bloomSpike",
      intensity: 2.3,
      threshold: 0.2,
    });
  }

  pushEffect(world, {
    kind: "ui.damageNumber",
    x,
    y,
    damage: value,
    source: "pellet",
  });
}

export function triggerBulletFired(
  world: World,
  _x: number,
  _y: number,
  isLocalPlayer: boolean = false,
): void {
  if (isLocalPlayer) {
    pushEffect(world, {
      kind: "webgl.bloomSpike",
      intensity: 2.1,
      threshold: 0.3,
    });

    pushEffect(world, {
      kind: "screen.shake",
      intensity: 1.0,
    });
  }
}

export function triggerBulletImpact(
  world: World,
  x: number,
  y: number,
  _vx: number = 0,
  _vy: number = 0,
): void {
  pushEffect(world, {
    kind: "webgl.gpuExplosion",
    x,
    y,
    intensity: 1.0,
    preset: "small",
  });

  pushEffect(world, {
    kind: "sound",
    sound: "impact",
  });

  pushEffect(world, {
    kind: "webgl.bloomSpike",
    intensity: 2.0,
    threshold: 0.25,
  });
}

export function triggerPlayerCollision(
  world: World,
  x: number,
  y: number,
  _vx: number = 0,
  _vy: number = 0,
): void {
  pushEffect(world, {
    kind: "webgl.gpuExplosion",
    x,
    y,
    intensity: 1.0,
    preset: "medium",
  });

  pushEffect(world, {
    kind: "screen.shake",
    intensity: 5.0,
  });

  pushEffect(world, {
    kind: "screen.flash",
    intensity: 0.3,
    r: 1.0,
    g: 1.0,
    b: 0.5,
  });

  pushEffect(world, {
    kind: "sound",
    sound: "impact",
  });
}
